import { cssJoin } from '@volvo-cars/css/utils';
import { ReactElement } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import bash from 'react-syntax-highlighter/dist/cjs/languages/hljs/bash';
import json from 'react-syntax-highlighter/dist/cjs/languages/hljs/json';
import xml from 'react-syntax-highlighter/dist/cjs/languages/hljs/xml';
import yaml from 'react-syntax-highlighter/dist/cjs/languages/hljs/yaml';
import { monoBlue } from 'react-syntax-highlighter/dist/cjs/styles/hljs';

import scrollbarStyle from '@collab/style/customThemeTokens/scrollbar.module.css';

import CopyButton from 'atoms/CopyButton';

import styles from './CodeHighlighter.module.css';

export type LanguageTypes = 'json' | 'yaml' | 'xml' | 'bash' | 'text';

SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('xml', xml);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('bash', bash);

export type CodeHighlighterProps = {
  language: LanguageTypes;
  wrapLongLines?: boolean;
  showLineNumbers?: boolean;
  children: string;
};

const CodeHighlighter: React.FC<CodeHighlighterProps> = ({
  language,
  wrapLongLines = false,
  showLineNumbers = false,
  children,
}) => {
  const code = children ? children.trim() : '';
  const classNames = cssJoin(styles.preTag, scrollbarStyle.thin);

  return (
    <Wrapper language={language} code={code}>
      <SyntaxHighlighter
        tabIndex="0"
        language={language}
        showLineNumbers={showLineNumbers}
        wrapLongLines={wrapLongLines}
        style={monoBlue}
        useInlineStyles={false}
        PreTag={(...props) => <pre {...props[0]} className={classNames} />}
      >
        {code}
      </SyntaxHighlighter>
    </Wrapper>
  );
};

const Wrapper: React.FC<{
  language: LanguageTypes;
  code: string;
  children: ReactElement;
}> = ({ language, code, children }) => {
  const classNames = cssJoin(
    styles.wrapper,
    'relative overflow-x-hidden overflow-y-auto mt-24 lg:mt-32',
    language === 'text' && styles.text,
    language === 'xml' && styles.xml,
  );

  return (
    <div className={classNames}>
      <CopyCodeButton>{code}</CopyCodeButton>
      {children}
    </div>
  );
};

const CopyCodeButton: React.FC<{
  children: string;
}> = ({ children }) => (
  <div
    className="absolute"
    style={{
      top: 16,
      insetInlineEnd: 16,
    }}
  >
    <CopyButton
      copy={children}
      tooltipPlacement="left"
      variant="filled"
      ariaLabel="copy code snippet"
    />
  </div>
);

export default CodeHighlighter;
